import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { LoadingState } from '@/components/ui/LoadingState';
import { ErrorState } from '@/components/ui/ErrorState';
import { usersApi } from '@/api/users.api';
import {
  assignmentMatrixApi,
  ASSIGNMENT_REGIONS,
  ASSIGNMENT_ROLES,
  type AssignmentRoleName
} from '@/api/assignmentMatrix.api';
import { CATEGORY_CODES, CATEGORY_LABEL, type CategoryCode } from '@/types/domain';
import { useToasts } from '@/context/ToastContext';
import { ApiError } from '@/lib/errors';

/**
 * V4.9.15 — Assignment Matrix editor (admin-only), opened from the Users
 * section. Lets ops set, per category × region, who is the Solver / Approver
 * / Communicator / Owner — and edit the country→region mapping — without a
 * code change. Saving applies to every NEW ticket immediately (the backend
 * resolver cache is invalidated on save). Existing tickets are untouched.
 */

const ROLE_LABEL: Record<AssignmentRoleName, string> = {
  solver: 'Solver',
  approver: 'Approver',
  communicator: 'Communicator',
  owner: 'Owner of the lead'
};

type Tab = AssignmentRoleName | 'regions';

const key = (cat: string, role: string, geo: string) => `${cat}|${role}|${geo}`;

interface Props {
  open: boolean;
  onClose: () => void;
}

export function AssignmentMatrixModal({ open, onClose }: Props) {
  const queryClient = useQueryClient();
  const { push } = useToasts();
  const [tab, setTab] = useState<Tab>('solver');
  // cell value encoding: '' = none/unassigned, 'tc' = ticket creator,
  // 'u:<id>' = a specific user.
  const [cells, setCells] = useState<Record<string, string>>({});
  const [regions, setRegions] = useState<Record<string, string>>({});

  const configQuery = useQuery({
    queryKey: ['assignment-matrix'],
    queryFn: () => assignmentMatrixApi.get(),
    enabled: open
  });
  const usersQuery = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list(),
    enabled: open
  });

  // Hydrate local editable state when the config arrives.
  useEffect(() => {
    if (!configQuery.data) return;
    const c: Record<string, string> = {};
    for (const r of configQuery.data.rules) {
      let v = '';
      if (r.special === 'ticket_creator') v = 'tc';
      else if (r.special === 'none') v = '';
      else if (r.assignee_user_id) v = `u:${r.assignee_user_id}`;
      c[key(r.category_code, r.role, r.geo_band)] = v;
    }
    setCells(c);
    const reg: Record<string, string> = {};
    for (const sr of configQuery.data.storeRegions) reg[sr.store_code] = sr.geo_band;
    setRegions(reg);
  }, [configQuery.data]);

  const users = useMemo(
    () => (usersQuery.data ?? []).slice().sort((a, b) => a.name.localeCompare(b.name)),
    [usersQuery.data]
  );

  const saveMutation = useMutation({
    mutationFn: () => {
      const rules: {
        category_code: string;
        role: string;
        geo_band: string;
        assignee_user_id: string | null;
        special: string | null;
      }[] = [];
      for (const cat of CATEGORY_CODES) {
        for (const role of ASSIGNMENT_ROLES) {
          for (const geo of ASSIGNMENT_REGIONS) {
            const v = cells[key(cat, role, geo)] ?? '';
            let assignee_user_id: string | null = null;
            let special: string | null = null;
            if (v === 'tc') special = 'ticket_creator';
            else if (v === '') special = 'none';
            else if (v.startsWith('u:')) assignee_user_id = v.slice(2);
            rules.push({ category_code: cat, role, geo_band: geo, assignee_user_id, special });
          }
        }
      }
      const storeRegions = Object.entries(regions).map(([store_code, geo_band]) => ({
        store_code,
        geo_band
      }));
      return assignmentMatrixApi.save({ rules, storeRegions });
    },
    onSuccess: (res) => {
      push({
        kind: 'success',
        title: 'Assignment matrix saved',
        message: `${res.rulesSaved} rules + ${res.regionsSaved} regions updated. Applies to new tickets now.`
      });
      void queryClient.invalidateQueries({ queryKey: ['assignment-matrix'] });
      onClose();
    },
    onError: (err: unknown) => {
      push({
        kind: 'error',
        title: 'Save failed',
        message: err instanceof ApiError ? err.problem?.detail ?? err.message : 'Unknown error'
      });
    }
  });

  const loading = configQuery.isLoading || usersQuery.isLoading;
  const errored = configQuery.isError || usersQuery.isError;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Assignment matrix — who handles what"
      size="xl"
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saveMutation.isPending || loading || errored}
            onClick={() => saveMutation.mutate()}
          >
            {saveMutation.isPending ? 'Saving…' : 'Save matrix'}
          </button>
        </>
      }
    >
      {loading ? (
        <LoadingState label="Loading matrix…" />
      ) : errored ? (
        <ErrorState
          error={configQuery.error ?? usersQuery.error}
          onRetry={() => {
            void configQuery.refetch();
            void usersQuery.refetch();
          }}
        />
      ) : (
        <div>
          <p className="text-small text-muted" style={{ marginTop: 0 }}>
            Set who is auto-assigned on each new ticket. Changes apply to new tickets immediately;
            tickets already created are not reassigned.
          </p>

          <div className="dashboard-tabs" style={{ marginBottom: 12, flexWrap: 'wrap' }}>
            {ASSIGNMENT_ROLES.map((r) => (
              <button
                key={r}
                type="button"
                className={`dashboard-tab ${tab === r ? 'is-active' : ''}`}
                onClick={() => setTab(r)}
              >
                <span className="dashboard-tab-label">{ROLE_LABEL[r]}</span>
              </button>
            ))}
            <button
              type="button"
              className={`dashboard-tab ${tab === 'regions' ? 'is-active' : ''}`}
              onClick={() => setTab('regions')}
            >
              <span className="dashboard-tab-label">Regions (countries)</span>
            </button>
          </div>

          {tab === 'regions' ? (
            <RegionEditor regions={regions} onChange={setRegions} />
          ) : (
            <RoleGrid role={tab} users={users} cells={cells} onChange={setCells} />
          )}
        </div>
      )}
    </Modal>
  );
}

function RoleGrid({
  role,
  users,
  cells,
  onChange
}: {
  role: AssignmentRoleName;
  users: { id: string; name: string; alias: string; active?: boolean }[];
  cells: Record<string, string>;
  onChange: React.Dispatch<React.SetStateAction<Record<string, string>>>;
}) {
  const set = (cat: string, geo: string, value: string) =>
    onChange((prev) => ({ ...prev, [key(cat, role, geo)]: value }));

  // The "nobody" option label differs per role for clarity.
  const noneLabel =
    role === 'approver' ? 'No approval' : role === 'communicator' ? 'No communicator' : 'Unassigned';

  return (
    <div className="table-wrapper">
      <table className="data-table">
        <thead>
          <tr>
            <th style={{ minWidth: 160 }}>Category</th>
            {ASSIGNMENT_REGIONS.map((geo) => (
              <th key={geo}>{geo}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {CATEGORY_CODES.map((cat) => (
            <tr key={cat} className="no-hover">
              <td>
                <span className="font-mono" style={{ fontWeight: 700 }}>
                  {cat}
                </span>
                <div className="text-small text-muted">
                  {CATEGORY_LABEL[cat as CategoryCode] ?? cat}
                </div>
              </td>
              {ASSIGNMENT_REGIONS.map((geo) => (
                <td key={geo}>
                  <select
                    className="form-select"
                    value={cells[key(cat, role, geo)] ?? ''}
                    onChange={(e) => set(cat, geo, e.target.value)}
                    style={{ minWidth: 140 }}
                  >
                    <option value="">{noneLabel}</option>
                    {role === 'communicator' ? (
                      <option value="tc">Ticket creator</option>
                    ) : null}
                    {users.map((u) => (
                      <option key={u.id} value={`u:${u.id}`}>
                        {u.name}
                        {u.active === false ? ' (inactive)' : ''}
                      </option>
                    ))}
                  </select>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RegionEditor({
  regions,
  onChange
}: {
  regions: Record<string, string>;
  onChange: React.Dispatch<React.SetStateAction<Record<string, string>>>;
}) {
  const codes = Object.keys(regions).sort();
  if (!codes.length) {
    return <div className="state"><h4>No store regions configured</h4></div>;
  }
  return (
    <div className="table-wrapper">
      <table className="data-table">
        <thead>
          <tr>
            <th>Country / store code</th>
            <th>Region</th>
          </tr>
        </thead>
        <tbody>
          {codes.map((code) => (
            <tr key={code} className="no-hover">
              <td>
                <span className="font-mono" style={{ fontWeight: 700 }}>
                  {code}
                </span>
              </td>
              <td>
                <select
                  className="form-select"
                  value={regions[code]}
                  onChange={(e) =>
                    onChange((prev) => ({ ...prev, [code]: e.target.value }))
                  }
                  style={{ minWidth: 160 }}
                >
                  {ASSIGNMENT_REGIONS.map((geo) => (
                    <option key={geo} value={geo}>
                      {geo}
                    </option>
                  ))}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
